import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Activity,
  Building2,
  CircleDollarSign,
  CreditCard,
  Landmark,
  RefreshCw,
  Search,
  Wallet
} from "lucide-react";
import { getApiUrl } from "../../config/api";

interface FinancialOverviewData {
  totals?: {
    feesCollected?: number;
    payrollExpenses?: number;
    subscriptionRevenue?: number;
    netPlatformFlow?: number;
  };
  subscriptions?: {
    active?: number;
    expired?: number;
    past_due?: number;
    cancelled?: number;
  };
  tiers?: {
    basic?: number;
    premium?: number;
    enterprise?: number;
    unassigned?: number;
  };
  schoolBreakdown?: Array<{
    id: number;
    name: string;
    status?: string | null;
    subscription_tier?: string | null;
    feesCollected?: number;
    payrollExpenses?: number;
    subscriptionRevenue?: number;
    subscriptionStatus?: string | null;
    subscriptionEnd?: string | null;
    userCount?: number;
  }>;
  recentSubscriptions?: Array<{
    id: number;
    tenantId: number;
    schoolName: string;
    amount?: number;
    currency?: string | null;
    status?: string | null;
    periodStart?: string | null;
    periodEnd?: string | null;
    createdAt?: string | null;
  }>;
}

const formatCurrency = (value: number | undefined, currency = "ZMW") =>
  new Intl.NumberFormat("en-ZM", {
    style: "currency",
    currency,
    maximumFractionDigits: 0
  }).format(Number(value || 0));

const formatDate = (value?: string | null) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleDateString("en-ZM", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

const statusTone = (value?: string | null) => {
  switch (String(value || "").toLowerCase()) {
    case "active":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "expired":
      return "border-rose-200 bg-rose-50 text-rose-700";
    case "past_due":
      return "border-amber-200 bg-amber-50 text-amber-700";
    case "cancelled":
    case "inactive":
      return "border-slate-200 bg-slate-100 text-slate-600";
    default:
      return "border-slate-200 bg-slate-50 text-slate-600";
  }
};

const FinancialOverview: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const [data, setData] = useState<FinancialOverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [schoolSearch, setSchoolSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "expired" | "past_due" | "cancelled">("all");

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadData = async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError("");
    try {
      const response = await axios.get(getApiUrl("superadmin/financial-overview"), authHeaders);
      setData(response.data || {});
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load financial overview.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const totals = data?.totals || {};
  const subscriptions = data?.subscriptions || {};
  const tiers = data?.tiers || {};
  const schoolBreakdown = data?.schoolBreakdown || [];
  const recentSubscriptions = data?.recentSubscriptions || [];
  const filteredSchools = schoolBreakdown.filter((school) => {
    const matchesSearch =
      !schoolSearch.trim() ||
      [school.name, school.subscription_tier, school.status, school.subscriptionStatus]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(schoolSearch.trim().toLowerCase());
    const matchesStatus =
      statusFilter === "all" || String(school.subscriptionStatus || "").toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalSchools = schoolBreakdown.length;
  const activeSchools = schoolBreakdown.filter((school) => String(school.status || "").toLowerCase() === "active").length;
  const topFeeSchool = schoolBreakdown.reduce((best, school) =>
    Number(school.feesCollected || 0) > Number(best?.feesCollected || 0) ? school : best,
  schoolBreakdown[0]);
  const topSubscriptionSchool = schoolBreakdown.reduce((best, school) =>
    Number(school.subscriptionRevenue || 0) > Number(best?.subscriptionRevenue || 0) ? school : best,
  schoolBreakdown[0]);

  const summaryCards = [
    {
      title: "Fees Collected",
      value: formatCurrency(totals.feesCollected),
      helper: "Tenant fee payment volume",
      Icon: Landmark,
      tone: "border-sky-200 bg-sky-50 text-sky-700"
    },
    {
      title: "Payroll Expenses",
      value: formatCurrency(totals.payrollExpenses),
      helper: "Recorded payroll outflow",
      Icon: Wallet,
      tone: "border-rose-200 bg-rose-50 text-rose-700"
    },
    {
      title: "Subscription Revenue",
      value: formatCurrency(totals.subscriptionRevenue),
      helper: "Tenant billing collections",
      Icon: CreditCard,
      tone: "border-emerald-200 bg-emerald-50 text-emerald-700"
    },
    {
      title: "Net Platform Flow",
      value: formatCurrency(totals.netPlatformFlow),
      helper: "Subscription revenue minus payroll",
      Icon: CircleDollarSign,
      tone: "border-violet-200 bg-violet-50 text-violet-700"
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
        <div className="mx-auto max-w-[1400px] space-y-6">
          <section className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
            <p className="text-[11px] font-black uppercase tracking-[0.28em] text-slate-400">Financial Overview</p>
            <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-950">Platform finance overview</h1>
            <p className="mt-3 text-sm text-slate-500">Loading financial telemetry for all schools.</p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
      <div className="mx-auto max-w-[1400px] space-y-6">
        <section className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.28em] text-slate-400">Financial Overview</p>
              <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-950">Platform finance overview</h1>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600">
                Review platform-wide finance signals across fee collections, payroll pressure, tenant subscriptions,
                and the schools generating the most activity.
              </p>
            </div>
            <button
              type="button"
              onClick={() => loadData(true)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
            >
              <RefreshCw className={refreshing ? "animate-spin" : ""} size={16} />
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
          {error ? (
            <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          ) : null}
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map(({ title, value, helper, Icon, tone }) => (
            <article key={title} className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">{title}</p>
                  <p className="mt-3 text-3xl font-black tracking-tight text-slate-950">{value}</p>
                  <p className="mt-2 text-sm text-slate-500">{helper}</p>
                </div>
                <div className={`rounded-2xl border p-3 ${tone}`}>
                  <Icon size={20} />
                </div>
              </div>
            </article>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Subscription Health</p>
                <h2 className="mt-2 text-xl font-black text-slate-950">Tenant subscription status</h2>
              </div>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">
                {totalSchools} schools
              </span>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {[
                ["Active", subscriptions.active || 0, "border-emerald-200 bg-emerald-50 text-emerald-700"],
                ["Expired", subscriptions.expired || 0, "border-rose-200 bg-rose-50 text-rose-700"],
                ["Past Due", subscriptions.past_due || 0, "border-amber-200 bg-amber-50 text-amber-700"],
                ["Cancelled", subscriptions.cancelled || 0, "border-slate-200 bg-slate-100 text-slate-700"]
              ].map(([label, value, tone]) => (
                <div key={String(label)} className={`rounded-2xl border px-4 py-5 ${tone}`}>
                  <p className="text-[11px] font-black uppercase tracking-[0.22em]">{label}</p>
                  <p className="mt-2 text-3xl font-black">{value}</p>
                </div>
              ))}
            </div>
          </article>

          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                <Activity size={18} />
              </div>
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Plan Distribution</p>
                <h2 className="text-xl font-black text-slate-950">Tenant package mix</h2>
              </div>
            </div>
            <div className="mt-6 space-y-4">
              {[
                ["Basic", tiers.basic || 0],
                ["Premium", tiers.premium || 0],
                ["Enterprise", tiers.enterprise || 0],
                ["Unassigned", tiers.unassigned || 0]
              ].map(([label, value]) => (
                <div key={String(label)}>
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span className="font-semibold text-slate-800">{label}</span>
                    <span>{value}</span>
                  </div>
                  <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-slate-900"
                      style={{ width: `${totalSchools ? Math.max((Number(value) / totalSchools) * 100, value ? 8 : 0) : 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              {activeSchools} of {totalSchools} schools are currently marked active across the platform.
            </div>
          </article>
        </section>

        <section className="grid gap-6 xl:grid-cols-2">
          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Top Collector</p>
            <h2 className="mt-2 text-xl font-black text-slate-950">{topFeeSchool?.name || "No data yet"}</h2>
            <p className="mt-3 text-sm text-slate-600">
              {topFeeSchool ? `${formatCurrency(topFeeSchool.feesCollected)} in recorded fee collections.` : "Fee collection data will appear once schools post payments."}
            </p>
          </article>
          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Top Subscription Account</p>
            <h2 className="mt-2 text-xl font-black text-slate-950">{topSubscriptionSchool?.name || "No data yet"}</h2>
            <p className="mt-3 text-sm text-slate-600">
              {topSubscriptionSchool ? `${formatCurrency(topSubscriptionSchool.subscriptionRevenue)} in subscription revenue tracked.` : "Subscription activity will appear once schools have billing records."}
            </p>
          </article>
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.6fr_0.9fr]">
          <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">School Breakdown</p>
                <h2 className="mt-2 text-xl font-black text-slate-950">Finance by school</h2>
              </div>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">
                Multi-school
              </span>
            </div>
            <div className="grid gap-3 border-b border-slate-200 px-6 py-4 lg:grid-cols-[minmax(0,1fr)_220px]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  value={schoolSearch}
                  onChange={(event) => setSchoolSearch(event.target.value)}
                  placeholder="Search schools, plan, or subscription status"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white"
                />
              </label>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-slate-300 focus:bg-white"
              >
                <option value="all">All subscription states</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="past_due">Past due</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            {filteredSchools.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">
                      <th className="px-6 py-4">School</th>
                      <th className="px-4 py-4">Status</th>
                      <th className="px-4 py-4">Plan</th>
                      <th className="px-4 py-4">Fees</th>
                      <th className="px-4 py-4">Payroll</th>
                      <th className="px-4 py-4">Subscription</th>
                      <th className="px-4 py-4">Users</th>
                      <th className="px-4 py-4">Renewal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredSchools.map((school) => (
                      <tr key={school.id} className="align-top text-slate-700">
                        <td className="px-6 py-4">
                          <div className="font-bold text-slate-900">{school.name}</div>
                          <div className="mt-1 text-xs text-slate-500">ID #{school.id}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col gap-2">
                            <span className={`inline-flex w-fit rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.2em] ${statusTone(school.status)}`}>
                              {school.status || "Unknown"}
                            </span>
                            <span className={`inline-flex w-fit rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.2em] ${statusTone(school.subscriptionStatus)}`}>
                              {school.subscriptionStatus || "No subscription"}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 font-semibold text-slate-800">{school.subscription_tier || "Unassigned"}</td>
                        <td className="px-4 py-4">{formatCurrency(school.feesCollected)}</td>
                        <td className="px-4 py-4">{formatCurrency(school.payrollExpenses)}</td>
                        <td className="px-4 py-4">{formatCurrency(school.subscriptionRevenue)}</td>
                        <td className="px-4 py-4">{Number(school.userCount || 0)}</td>
                        <td className="px-4 py-4">{formatDate(school.subscriptionEnd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-6 py-12 text-center text-sm text-slate-500">
                {schoolBreakdown.length ? "No schools match the current search or status filter." : "No school finance data is available yet."}
              </div>
            )}
          </article>

          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                <Building2 size={18} />
              </div>
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Recent Activity</p>
                <h2 className="text-xl font-black text-slate-950">Latest subscriptions</h2>
              </div>
            </div>
            {recentSubscriptions.length ? (
              <div className="mt-6 space-y-4">
                {recentSubscriptions.map((entry) => (
                  <div key={entry.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-bold text-slate-900">{entry.schoolName}</p>
                        <p className="mt-1 text-xs text-slate-500">Tenant #{entry.tenantId}</p>
                      </div>
                      <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.2em] ${statusTone(entry.status)}`}>
                        {entry.status || "Unknown"}
                      </span>
                    </div>
                    <div className="mt-4 grid gap-3 text-sm text-slate-600">
                      <div className="flex items-center justify-between gap-3">
                        <span>Amount</span>
                        <span className="font-bold text-slate-900">{formatCurrency(entry.amount, entry.currency || "ZMW")}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span>Period</span>
                        <span className="text-right">{formatDate(entry.periodStart)} to {formatDate(entry.periodEnd)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span>Recorded</span>
                        <span>{formatDate(entry.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                No subscription activity has been recorded yet.
              </div>
            )}
          </article>
        </section>
      </div>
    </div>
  );
};

export default FinancialOverview;
